import { useEffect, useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, Input, Textarea } from '@/design-system';
import type { CreateServerData, UpdateServerData, WhatsAppServer } from '../api/whatsapp-servers.service';
import { emptyServerForm } from '../utils/server-form';

interface WhatsAppServerFormDialogProps {
  mode: 'create' | 'edit';
  isOpen: boolean;
  server: WhatsAppServer | null;
  onClose: () => void;
  createServer: UseMutationResult<unknown, unknown, CreateServerData, unknown>;
  updateServer: UseMutationResult<unknown, unknown, { id: string; data: UpdateServerData }, unknown>;
}

export function WhatsAppServerFormDialog({
  mode,
  isOpen,
  server,
  onClose,
  createServer,
  updateServer,
}: WhatsAppServerFormDialogProps) {
  const [formData, setFormData] = useState<CreateServerData>(emptyServerForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && server) {
      setFormData({
        name: server.name,
        baseUrl: server.baseUrl,
        adminApiKey: server.adminApiKey,
        maxCapacity: server.maxCapacity,
        region: server.region || '',
        priority: server.priority,
        notes: server.notes || '',
        isActive: server.isActive,
      });
    } else if (mode === 'create') {
      setFormData(emptyServerForm());
    }
    setFormErrors({});
  }, [isOpen, mode, server]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Nome é obrigatório';
    else if (formData.name.length < 2) errors.name = 'Nome deve ter no mínimo 2 caracteres';
    if (!formData.baseUrl.trim()) errors.baseUrl = 'URL é obrigatória';
    else if (!formData.baseUrl.match(/^https?:\/\/.+/)) {
      errors.baseUrl = 'URL inválida (deve começar com http:// ou https://)';
    }
    if (mode === 'create' && !formData.adminApiKey.trim()) errors.adminApiKey = 'API Key é obrigatória';
    if (formData.maxCapacity !== undefined && formData.maxCapacity < 1) {
      errors.maxCapacity = 'Capacidade máxima deve ser positiva';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClose = () => {
    setFormData(emptyServerForm());
    setFormErrors({});
    onClose();
  };

  const isPending = mode === 'create' ? createServer.isPending : updateServer.isPending;

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (mode === 'create') {
      try {
        await createServer.mutateAsync(formData);
        handleClose();
      } catch {
        /* toast */
      }
      return;
    }
    if (!server) return;
    const updateData: UpdateServerData = {
      name: formData.name,
      baseUrl: formData.baseUrl,
      maxCapacity: formData.maxCapacity,
      region: formData.region || undefined,
      priority: formData.priority,
      notes: formData.notes || undefined,
      isActive: formData.isActive,
    };
    if (formData.adminApiKey.trim()) {
      updateData.adminApiKey = formData.adminApiKey;
    }
    try {
      await updateServer.mutateAsync({ id: server.id, data: updateData });
      handleClose();
    } catch {
      /* toast */
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'Novo Servidor UAZAPI' : 'Editar Servidor'}
      size="lg"
      footer={(
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose} disabled={isPending}>Cancelar</Button>
          <Button variant="primary" onClick={() => void handleSubmit()} isLoading={isPending}>
            {mode === 'create' ? 'Criar' : 'Salvar'}
          </Button>
        </div>
      )}
    >
      <div className="space-y-4">
        <Input
          label="Nome"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          error={formErrors.name}
          placeholder="Ex: Cluster Alpha 01"
          required
        />
        <Input
          label="URL Base"
          value={formData.baseUrl}
          onChange={(e) => setFormData((prev) => ({ ...prev, baseUrl: e.target.value }))}
          error={formErrors.baseUrl}
          placeholder="https://wpp01.orya.com.br"
          required
        />
        <Input
          label="Admin API Key"
          type="password"
          value={formData.adminApiKey}
          onChange={(e) => setFormData((prev) => ({ ...prev, adminApiKey: e.target.value }))}
          error={formErrors.adminApiKey}
          placeholder={mode === 'edit' ? 'Deixe em branco para não alterar' : 'Chave de API'}
          required={mode === 'create'}
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Capacidade Máxima"
            type="number"
            value={formData.maxCapacity}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, maxCapacity: parseInt(e.target.value, 10) || 300 }))
            }
            error={formErrors.maxCapacity}
            placeholder="300"
            required
          />
          <Input
            label="Prioridade"
            type="number"
            value={formData.priority}
            onChange={(e) => setFormData((prev) => ({ ...prev, priority: parseInt(e.target.value, 10) || 0 }))}
            placeholder="0"
          />
        </div>
        <Input
          label="Região"
          value={formData.region}
          onChange={(e) => setFormData((prev) => ({ ...prev, region: e.target.value }))}
          placeholder="Ex: sa-east-1"
        />
        <Textarea
          label="Notas"
          value={formData.notes}
          onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
          placeholder="Notas administrativas (opcional)"
          rows={3}
        />
        <Checkbox
          id={mode === 'create' ? 'isActive' : 'isActiveEdit'}
          label={mode === 'create' ? 'Servidor ativo (pode receber novos canais)' : 'Servidor ativo'}
          checked={formData.isActive}
          onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
        />
      </div>
    </Dialog>
  );
}
